// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'
import type { Importance } from '../shared'

/**
 * The `<qds-divider>` element a thematic break between paragraph-level
 * elements: for example, a change of scene in a story, or a shift of topic
 * within a section.
 *
 * Dividers can also be used to group menu items in `<qds-dropdown>` elements.
 *
 * @see https://quartz.se.com/build/components/divider
 */
@Component({
  tag: 'qds-divider',
  shadow: true,
  styleUrl: 'divider.css',
})
export class Divider implements ComponentInterface {
  /**
   * The divider's importance.
   */
  @Prop() public readonly importance?: Exclude<Importance, 'subdued'> =
    'standard'

  /**
   * Display the divider in vertical orientation.
   */
  @Prop() public readonly vertical: boolean = false

  @Element() private readonly host!: HTMLQdsDividerElement

  #inheritedAttributes: Attributes = {}

  get #computedImportance(): Importance {
    switch (this.importance) {
      case 'emphasized': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <hr
        class={{
          'qds-divider': true,
          'qds-vertical': this.vertical,
        }}
        aria-hidden="true"
        data-importance={this.#computedImportance}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      />
    )
  }
}
